import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { apiGet } from '../lib/api';
import { AuditTable, type AuditRow } from '../components/AuditTable';

type AuditResponse = {
  rows: AuditRow[];
  limit: number;
  before: string | null;
};

const PAGE_SIZE = 50;

export function Audit() {
  const { me } = useAuth();
  const tenant = me?.tenants[0];

  const [rows, setRows] = useState<AuditRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const fetchPage = useCallback(
    async (before?: string) => {
      if (!tenant) return;
      const params = new URLSearchParams({ tenantSlug: tenant.slug, limit: String(PAGE_SIZE) });
      if (before) params.set('before', before);
      const data = await apiGet<AuditResponse>(`/api/audit?${params.toString()}`);
      return data;
    },
    [tenant],
  );

  // Initial load
  useEffect(() => {
    if (!tenant) return;
    setLoading(true);
    setError(null);
    fetchPage()
      .then((data) => {
        if (!data) return;
        setRows(data.rows);
        setHasMore(data.rows.length === PAGE_SIZE);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [tenant, fetchPage]);

  const handleLoadOlder = useCallback(async () => {
    if (rows.length === 0) return;
    const oldest = rows[rows.length - 1].created_at;
    setLoadingMore(true);
    try {
      const data = await fetchPage(oldest);
      if (!data) return;
      setRows((prev) => [...prev, ...data.rows]);
      setHasMore(data.rows.length === PAGE_SIZE);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingMore(false);
    }
  }, [rows, fetchPage]);

  if (!tenant) {
    return (
      <main className="min-h-screen px-6 py-12">
        <div className="mx-auto max-w-5xl">
          <BackLink />
          <section className="mt-8 rounded-lg border border-amber-700/40 bg-amber-900/10 p-6">
            <h2 className="text-lg font-semibold">No workspace yet</h2>
            <p className="mt-2 text-slate-400">
              You need an active workspace to view the audit trail.{' '}
              <a className="text-sky-400 hover:underline" href="https://clarity.app/signup">
                Start with Pilot — $149/mo
              </a>
              .
            </p>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen px-6 py-12">
      <div className="mx-auto max-w-5xl">
        <BackLink />

        <header className="mt-4">
          <p className="text-xs uppercase tracking-widest text-sky-400">{tenant.name}</p>
          <h1 className="mt-1 text-2xl font-semibold">Audit trail</h1>
          <p className="mt-1 text-slate-400 text-sm">
            All actions recorded for this workspace, newest first.
          </p>
        </header>

        <section className="mt-8">
          {error && (
            <p className="text-red-400 text-sm" role="alert">
              Failed to load audit trail: {error}
            </p>
          )}
          {!error && loading && (
            <p className="text-slate-500 text-sm">Loading…</p>
          )}
          {!error && !loading && (
            <AuditTable
              rows={rows}
              onLoadOlder={handleLoadOlder}
              isLoadingMore={loadingMore}
              hasMore={hasMore}
            />
          )}
        </section>
      </div>
    </main>
  );
}

function BackLink() {
  return (
    <Link to="/dashboard" className="text-sm text-slate-400 hover:text-slate-200">
      ← Dashboard
    </Link>
  );
}
